import { useCallback, useEffect, useMemo, useState } from 'react';
import { useInfiniteQuery, type InfiniteData, type QueryKey } from '@tanstack/react-query';
import { API_BASE } from './api-base';
import { apiFetch } from './api-client';
import { queryClient, queryKeys } from './query-client';
import { clearChatsCache, readChatsCache, writeChatsCache } from './chats-cache';
import type { Conversation } from './conversations';
import { invalidateChatReadResponses } from './chat-session';
import { parseConversationReadState, type ConversationReadState } from './chat-read-state';

export type { ConversationReadState } from './chat-read-state';
export interface ChatListRow extends Conversation {
  id: string;
  readState: ConversationReadState;
  messageId?: string;
  matchedSnippet?: string;
  lastMessagePreview?: string;
}
export interface ChatListPage { conversations: ChatListRow[]; nextCursor: string | null; asOf: string }
export class ChatsNotSupportedError extends Error {}

// TanStack refetches every retained cursor page in sequence. Keep automatic work
// bounded; a deeper list stays in place until its reader explicitly refreshes it.
export const MAX_AUTOMATIC_CHAT_PAGES = 5;
export function canAutomaticallyRefreshChats(data: InfiniteData<ChatListPage> | undefined): boolean {
  return (data?.pages.length ?? 0) <= MAX_AUTOMATIC_CHAT_PAGES;
}

/** Explicit refresh starts a new cursor chain, without mixing new and old pages. */
export async function refreshChatsFromStart(key: QueryKey, refetch: () => Promise<unknown>): Promise<void> {
  notificationRefreshGeneration += 1;
  await queryClient.cancelQueries({ queryKey: key, exact: true });
  queryClient.setQueryData<InfiniteData<ChatListPage>>(key, (current) => current && ({
    pages: current.pages.slice(0, 1), pageParams: [undefined],
  }), { updatedAt: 0 });
  // The deep observer has automatic fetching disabled; its explicit refetch
  // still works and also deduplicates an already-started first-page request.
  await refetch();
}

let generation = 0;
let notificationRefreshGeneration = 0;
let acceptedNotificationRefreshGeneration = 0;
let refreshTimer: ReturnType<typeof setTimeout> | undefined;
/** Coalesce realtime bursts into one combined read, without a polling loop or per-row notification reads. */
export function scheduleChatsInvalidation(): void {
  invalidateChatReadResponses();
  if (refreshTimer) return;
  refreshTimer = setTimeout(() => { refreshTimer = undefined; invalidateChats(); }, 600);
}
export function invalidateChats(): void {
  notificationRefreshGeneration += 1;
  clearTimeout(refreshTimer);
  refreshTimer = undefined;
  // The persisted first page is NOT cleared here: this runs on every realtime event, send and read
  // receipt, and only the list's own fetch rewrites the page, so clearing left the next visit with
  // nothing to paint and a full-page loader. A saved page is revalidated on mount anyway.
  cancelChatsReads();
  // Deep lists are invalidated too, but must not launch an unbounded background
  // walk or lose their saved scroll position. Fresh read receipts still patch rows.
  void queryClient.invalidateQueries({ queryKey: queryKeys.chatsAll, refetchType: 'none' });
  void queryClient.refetchQueries({ queryKey: queryKeys.chatsAll, type: 'active',
    predicate: (query) => canAutomaticallyRefreshChats(query.state.data as InfiniteData<ChatListPage> | undefined),
  }, { cancelRefetch: false });
}
export function cancelChatsReads(): void {
  generation += 1;
  void queryClient.cancelQueries({ queryKey: queryKeys.chatsAll });
}

/** Apply a confirmed metadata write without changing the independently-versioned read tuple. */
export function patchChatsConversation(id: string, patch: Partial<Conversation> | null): void {
  queryClient.setQueriesData<InfiniteData<ChatListPage>>({ queryKey: queryKeys.chatsAll }, (current) => current && ({ ...current,
    pages: current.pages.map((page) => ({ ...page, conversations: patch === null
      ? page.conversations.filter((row) => row.id !== id)
      : page.conversations.map((row) => row.id === id ? { ...row, ...patch, id: row.id } : row) })),
  }));
  // A deleted or renamed row must not repaint from disk on the next visit.
  void clearChatsCache();
  invalidateChats();
}

export function applyChatsAcknowledgment(owner: string, id: string, readState: ConversationReadState): void {
  invalidateChats();
  queryClient.setQueriesData<InfiniteData<ChatListPage>>({ queryKey: [...queryKeys.chatsAll, owner] }, (current) => current && ({ ...current,
    pages: current.pages.map((page) => ({ ...page, conversations: page.conversations.map((row) => row.id === id ? { ...row, readState } : row) })),
  }));
}

/**
 * The chat's bell rows were just marked read (`conversation-notification-read.ts`): drop the row's
 * notifications half of the unread dot now instead of on the next 30s poll, then re-read the list with a
 * fresh notification state so the server's own answer replaces the patch. The messages half is untouched.
 */
export async function applyChatsNotificationsRead(id: string): Promise<void> {
  // Cancel first: a list read in flight (the 30s poll) is cancelled with `revert`, which restores its
  // fetch-start snapshot — a patch written before that would be reverted and the dot relit.
  cancelChatsReads();
  await queryClient.cancelQueries({ queryKey: queryKeys.chatsAll });
  queryClient.setQueriesData<InfiniteData<ChatListPage>>({ queryKey: queryKeys.chatsAll }, (current) => current && ({ ...current,
    pages: current.pages.map((page) => ({ ...page, conversations: page.conversations.map((row) => row.id === id && row.readState.hasUnreadNotifications
      ? { ...row, readState: { ...row.readState, hasUnreadNotifications: false } } : row) })),
  }));
  invalidateChats();
}

export function mergeChatPages(pages: ChatListPage[]): ChatListRow[] {
  const rows = new Map<string, ChatListRow>();
  for (const page of pages) for (const row of page.conversations) rows.set(row.id, row);
  return [...rows.values()];
}

export async function fetchChatsPage(query: string, channel: string, cursor: string | undefined, signal?: AbortSignal): Promise<ChatListPage> {
  const started = generation;
  const notificationRefresh = notificationRefreshGeneration;
  const params = new URLSearchParams({ contract: 'chats-v1', limit: '40' });
  if (notificationRefresh > acceptedNotificationRefreshGeneration) params.set('refreshNotificationState', 'true');
  if (query) params.set('q', query);
  if (channel) params.set('channel', channel);
  if (cursor) params.set('cursor', cursor);
  const response = await apiFetch(`${API_BASE}/api/conversations?${params}`, { credentials: 'include', signal });
  if (response.status === 501) {
    const body = await response.json().catch(() => null) as { error?: string } | null;
    if (body?.error === 'chats_not_supported') throw new ChatsNotSupportedError('chats_not_supported');
  }
  if (!response.ok) throw new Error(`Chats request failed (${response.status})`);
  const page = await response.json() as ChatListPage;
  if (started !== generation || signal?.aborted) throw new DOMException('Stale chats response', 'AbortError');
  if (!Array.isArray(page.conversations) || typeof page.asOf !== 'string'
    || page.conversations.some((row) => typeof row?.id !== 'string' || !row.readState)) throw new Error('Unsupported chats response');
  page.conversations = page.conversations.map((row) => ({ ...row, readState: parseConversationReadState(row.readState) }));
  // Failed, cancelled and superseded reads must not consume a bell/Clear all
  // invalidation. A later invalidation also remains pending for the next read.
  acceptedNotificationRefreshGeneration = Math.max(acceptedNotificationRefreshGeneration, notificationRefresh);
  return page;
}

export function useChats(query: string, channel: string, owner: string | null) {
  const trimmed = query.trim();
  const [debounced, setDebounced] = useState(trimmed);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(trimmed), 250);
    return () => clearTimeout(timer);
  }, [trimmed]);
  const key = useMemo(() => queryKeys.chats(owner, debounced, channel), [owner, debounced, channel]);
  const entry = useMemo(() => ({ key }), [key]);
  const [confirmedEntry, setConfirmedEntry] = useState<object | null>(null);
  // The first network read waits for the disk read. Started together, the fetch's revert snapshot has no
  // data, so any cancel during it (a read post, a realtime event) wiped the painted page back to the loader.
  const [hydratedKey, setHydratedKey] = useState<QueryKey | null>(null);
  const hydrated = !owner || Boolean(debounced) || hydratedKey === key || queryClient.getQueryData(key) !== undefined;
  useEffect(() => {
    if (!owner || debounced) return;
    let cancelled = false;
    const started = generation;
    void readChatsCache(owner, channel).then((snapshot) => {
      if (cancelled) return;
      if (snapshot && started === generation && !queryClient.getQueryData(key)) {
        // Always stale: the observer is enabled only after this, past its refetchOnMount, so a fresh
        // `updatedAt` would skip the first network read — leaving the cursor-less disk page (≤40 rows)
        // and its notification state in place for up to staleTime.
        queryClient.setQueryData<InfiniteData<ChatListPage>>(key,
          { pages: [snapshot.page], pageParams: [undefined] }, { updatedAt: 0 });
      }
      setHydratedKey(key);
    });
    return () => { cancelled = true; };
  }, [owner, debounced, channel, key]);
  const result = useInfiniteQuery({
    queryKey: key,
    // refetchOnMount alone does not cover switching back to a cached filter in
    // the same observer: TanStack's stale-key transition also needs this gate.
    // Explicit fetchNextPage/refetch remain available while automatic work pauses.
    enabled: Boolean(owner) && hydrated && canAutomaticallyRefreshChats(queryClient.getQueryData<InfiniteData<ChatListPage>>(key)),
    queryFn: async ({ pageParam, signal }) => {
      const page = await fetchChatsPage(debounced, channel, pageParam, signal);
      // Paging must not renew the persisted age of an unchanged first page.
      if (owner && !debounced && !pageParam) void writeChatsCache(owner, channel, page);
      return page;
    },
    getNextPageParam: (last: ChatListPage, pages: ChatListPage[]) => {
      const cursor = last.nextCursor;
      return !debounced && cursor && !pages.slice(0, -1).some((page) => page.nextCursor === cursor) ? cursor : undefined;
    },
    staleTime: 30_000,
    // Kept warm across in-app navigation: returning to Chats repaints instantly and revalidates in the
    // background instead of showing the loader. Bounded by MAX_AUTOMATIC_CHAT_PAGES.
    cacheTime: 60 * 60_000,
    refetchOnMount: (query) => canAutomaticallyRefreshChats(query.state.data),
    refetchOnWindowFocus: (query) => canAutomaticallyRefreshChats(query.state.data),
    refetchOnReconnect: (query) => canAutomaticallyRefreshChats(query.state.data),
    // Reader records have no domain events. Within the automatic budget, polling
    // converges another device's message-only reads. Deep lists expose Refresh.
    refetchInterval: (data) => canAutomaticallyRefreshChats(data) ? 30_000 : false,
    onSuccess: () => {
      // This observer callback only runs for successful network fetches, never
      // setQueryData/IndexedDB hydration. Cached emptiness cannot trigger navigation.
      setConfirmedEntry(entry);
    },
  });
  useEffect(() => {
    // A fresh in-memory empty page would otherwise skip the mount fetch for 30s.
    // Confirm it for this entry, including when returning from another filter.
    if (owner && confirmedEntry !== entry && !result.isFetching && !result.isError
      && result.data && result.data.pages.every((page) => page.conversations.length === 0)) {
      void result.refetch({ cancelRefetch: false });
    }
  }, [owner, entry, confirmedEntry, result.data, result.isFetching, result.isError, result.refetch]);
  const refreshFromStart = useCallback(() => refreshChatsFromStart(key, () => result.refetch({ cancelRefetch: false })), [key, result.refetch]);
  const rows = useMemo(() => mergeChatPages(result.data?.pages ?? []), [result.data]);
  return { ...result, rows, refreshFromStart, automaticRefreshPaused: !canAutomaticallyRefreshChats(result.data),
    unsupported: result.error instanceof ChatsNotSupportedError, debouncing: debounced !== trimmed,
    serverReady: confirmedEntry === entry && result.isSuccess && !result.isFetching };
}
